import { Card } from "./Card";
import { PlugIcon, RefreshIcon } from "./Icons";
import { CHIP_LIST, type ChipKey } from "../flasher/chips";
import type { ConnectionState } from "../hooks/useFlasher";
import type { DeviceInfo } from "../flasher/flashService";

interface Props {
  connection: ConnectionState;
  device: DeviceInfo | null;
  monitorActive: boolean;
  assumedChipKey: ChipKey;
  supported: boolean;
  /** A port is already granted, so Connect will not re-prompt. */
  hasPort: boolean;
  onSelectAssumedChip: (key: ChipKey) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onReset: () => void;
}

export function DeviceCard({
  connection,
  device,
  monitorActive,
  assumedChipKey,
  supported,
  hasPort,
  onSelectAssumedChip,
  onConnect,
  onDisconnect,
  onReset,
}: Props) {
  const connected = connection === "connected";
  const connecting = connection === "connecting";

  return (
    <Card
      step={1}
      done={connected}
      title="Board"
      hint={connected ? "Connected and in download mode." : "Put the board in download mode and connect."}
      actions={
        connected ? (
          <span className="pill pill--success">
            <span className="dot" /> Connected
          </span>
        ) : monitorActive ? (
          <span className="pill pill--info">
            <span className="dot dot--pulse" /> Monitoring
          </span>
        ) : connecting ? (
          <span className="pill pill--warning">
            <span className="dot dot--pulse" /> Connecting
          </span>
        ) : (
          <span className="pill pill--muted">Not connected</span>
        )
      }
    >
      {connected && device ? (
        <>
          <div className="kv">
            <span className="kv__key">Chip</span>
            <span className="kv__value">{device.description}</span>
            <span className="kv__key">Flash size</span>
            <span className="kv__value">{device.flashSize ?? "unknown"}</span>
            <span className="kv__key">USB device</span>
            <span className="kv__value">{device.portInfo || "n/a"}</span>
            {device.usingNativeUsb && (
              <>
                <span className="kv__key">Interface</span>
                <span className="kv__value">native USB</span>
              </>
            )}
          </div>

          {!device.chip && (
            <div className="notice notice--warning">
              <span className="notice__icon">
                <RefreshIcon size={16} />
              </span>
              <div className="notice__body">
                This chip was detected but is not in the built-in profile list, so offsets are not being
                auto-corrected for it. Check each offset in the file list before flashing.
              </div>
            </div>
          )}

          <div className="row">
            <button className="btn" onClick={onReset}>
              <RefreshIcon size={15} /> Reset board
            </button>
            <button className="btn btn--danger" onClick={onDisconnect}>
              Disconnect
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label className="field__label" htmlFor="assumed-chip">
              Target board (until one is connected)
            </label>
            <select
              id="assumed-chip"
              value={assumedChipKey}
              onChange={(event) => onSelectAssumedChip(event.target.value as ChipKey)}
            >
              {CHIP_LIST.map((chip) => (
                <option key={chip.key} value={chip.key}>
                  {chip.name}
                </option>
              ))}
            </select>
            <span className="field__help">
              Bootloader offsets differ per chip, so this is used to place your files until the real chip is
              detected. Connecting overrides it.
            </span>
          </div>

          <button className="btn btn--primary btn--block" onClick={onConnect} disabled={connecting || !supported}>
            <PlugIcon size={17} />
            {connecting ? "Connecting..." : "Connect to board"}
          </button>

          {hasPort && (
            <button className="btn btn--ghost btn--sm" onClick={onDisconnect}>
              Use a different port
            </button>
          )}

          <p className="muted">
            If the board does not respond: hold <strong>BOOT</strong>, tap <strong>EN/RST</strong>, release{" "}
            <strong>BOOT</strong>, then connect. Close the Arduino Serial Monitor first — it holds the port open.
          </p>
        </>
      )}
    </Card>
  );
}
